import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import GridBackground from "@/components/primitives/GridBackground";

const FAQ_ITEMS = [
    {
        q: "What is The Creator's Agent?",
        a: "The Creator's Agent is the structured A–Z system for building intelligent systems correctly. It is the first public gateway into the broader Smithers ecosystem.",
    },
    {
        q: "What does A–H mean? What about I–Z?",
        a: "A–H is the operational core, deployable today — eight disciplines from architecture through scale. I–Z is the advanced doctrine layer: integration systems, optimization, protection, infrastructure, and the path to ecosystem mastery (ending at Zenith). Both layers are mapped. The system is structured end-to-end.",
    },
    {
        q: "What is \u201CBuild Path Access\u201D?",
        a: "A structured progressive unlock system. You can unlock one letter, a phase pair (e.g., A+B), or the full A–H system at once. You may unlock the next letter or phase early — there are no waiting periods.",
    },
    {
        q: "Is this financing?",
        a: "No. Build Path Access is structured access to a disciplined system, not a financing or installment plan. You buy what you need, when you need it, and own it.",
    },
    {
        q: "Does this work offline?",
        a: "Yes. The system ships as a downloadable filesystem — A–H folders, START_HERE launcher, prerecorded Smithers guidance. Online unlocks the live dashboard, Q-Band tracking, and ecosystem features.",
    },
    {
        q: "What is QUADRA Core® OS?",
        a: "QUADRA Core® is the disciplined reasoning layer that powers the ecosystem. It validates inputs, structures execution, and provides multi-perspective analysis.",
    },
    {
        q: "What is Q-Band?",
        a: "Q-Band is the bandwidth allocation system that replaces tokens. Continuous operation, no hard cutoffs, adaptive throughput.",
    },
    {
        q: "How does this connect to the broader ecosystem?",
        a: "The Creator's Agent is one node in a connected intelligence ecosystem that includes Smithers XI®, QUADRA Core®, Q-Band, and Creator's Stake. Once you're in, deeper layers become available.",
    },
];

function FaqItem({ item, index, open, onToggle }) {
    return (
        <div
            data-testid={`faq-item-${index}`}
            className={`relative transition-all duration-500 ${open ? "bg-tca-bg-panel/55" : "bg-tca-bg-panel/15 hover:bg-tca-bg-panel/35"}`}
            style={{
                border: open ? "1px solid rgba(0,224,232,0.28)" : "1px solid rgba(0,224,232,0.08)",
            }}
        >
            {/* Corner brackets — only on open */}
            {open && (
                <>
                    {[
                        { top: 0, left: 0, borderTop: "1px solid var(--tca-cyan-core)", borderLeft: "1px solid var(--tca-cyan-core)" },
                        { top: 0, right: 0, borderTop: "1px solid var(--tca-cyan-core)", borderRight: "1px solid var(--tca-cyan-core)" },
                        { bottom: 0, left: 0, borderBottom: "1px solid var(--tca-cyan-core)", borderLeft: "1px solid var(--tca-cyan-core)" },
                        { bottom: 0, right: 0, borderBottom: "1px solid var(--tca-cyan-core)", borderRight: "1px solid var(--tca-cyan-core)" },
                    ].map((style, i) => (
                        <span
                            key={i}
                            aria-hidden="true"
                            className="absolute pointer-events-none w-3 h-3"
                            style={{ ...style, boxShadow: "0 0 6px var(--tca-cyan-core)" }}
                        />
                    ))}
                </>
            )}

            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                data-testid={`faq-toggle-${index}`}
                className="
                    relative w-full text-left flex items-start gap-5
                    py-5 sm:py-6 px-5 sm:px-7
                    transition-colors duration-300
                "
            >
                <span className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-tca-cyan/85 mt-1 flex-shrink-0">
                    {`0${index + 1}`}
                </span>
                <span className="flex-1 font-display text-lg sm:text-xl lg:text-[22px] tracking-[-0.005em] text-tca-ink leading-snug pr-4">
                    {item.q}
                </span>
                <motion.span
                    animate={{ rotate: open ? 45 : 0, color: open ? "var(--tca-cyan-core)" : "rgba(110,247,255,0.85)" }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="flex-shrink-0 mt-1.5"
                    style={{
                        filter: open ? "drop-shadow(0 0 8px rgba(0,224,232,0.55))" : "none",
                    }}
                >
                    <Plus size={22} strokeWidth={1.6} />
                </motion.span>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 sm:px-7 pb-6 sm:pb-7 pl-[60px] sm:pl-[78px] relative">
                            {/* Cyan underline draw-in */}
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: "60%" }}
                                exit={{ width: 0 }}
                                transition={{ duration: 0.55, delay: 0.1 }}
                                className="h-px bg-gradient-to-r from-tca-cyan/60 to-transparent mb-4"
                            />
                            <p className="text-tca-ink-muted text-base leading-relaxed max-w-2xl">
                                {item.a}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="relative bg-tca-deep overflow-hidden"
        >
            <GridBackground />

            <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-28 sm:py-40">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                        className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.36em] text-tca-cyan/85 mb-5"
                        data-testid="faq-eyebrow"
                    >
                        // Frequently Asked
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="faq-headline"
                        className="font-display text-[44px] leading-[1.04] sm:text-[64px] sm:leading-[1.0] lg:text-[80px] lg:leading-[0.98] tracking-[-0.025em] text-tca-ink"
                    >
                        Questions, answered.
                    </motion.h2>
                </div>

                {/* Accordion */}
                <div className="mt-16 sm:mt-20 max-w-3xl mx-auto space-y-3">
                    {FAQ_ITEMS.map((item, i) => (
                        <FaqItem
                            key={i}
                            item={item}
                            index={i}
                            open={openIndex === i}
                            onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
